import { Users, MessageCircle, MapPin, Calendar, GripVertical, Pin, PartyPopper, ChevronDown, ChevronRight, Folder, LayoutGrid, Building2 } from 'lucide-react';
import { useTranslation } from '../../context/LanguageContext';
import { useState, useEffect, useRef } from 'react';
import { getUnreadCount } from '../../services/messagesService';
import { getModules, getPeopleModules, MODULE_NAV_MAP, toggleModulePinned, addModuleToGroup, removeModuleFromGroup } from '../../services/modulesService';
import { getPeoplePreferences, savePeoplePreferences } from '../../services/apiService';

/**
 * Modules Component - Grid of available modules with drag & drop to sidebar
 * Displays modules organized by groups
 * @param {Object} props
 * @param {Function} props.onNavigate - Navigation handler
 */
const Modules = ({ onNavigate }) => {
    const { t, language } = useTranslation();
    const [unreadMessages, setUnreadMessages] = useState(0);
    const [loading, setLoading] = useState(true);

    // Helper to normalize object keys to lowercase (API returns uppercase)
    const normalizeKeys = (obj) => {
        if (!obj || typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) return obj.map(normalizeKeys);
        return Object.keys(obj).reduce((acc, key) => {
            const lowerKey = key.toLowerCase();
            acc[lowerKey] = normalizeKeys(obj[key]);
            return acc;
        }, {});
    };

    // All modules from API
    const [allModules, setAllModules] = useState([]);
    // User's module groups
    const [moduleGroups, setModuleGroups] = useState([]);
    // Collapsed groups state (local)
    const [collapsedGroups, setCollapsedGroups] = useState({});
    // Drag over group state (for visual feedback)
    const [dragOverGroupId, setDragOverGroupId] = useState(null);

    // Unified pinned items (modules and groups)
    // Format: [{ type: 'module', id: 'contacts' }, { type: 'group', id: 123 }]
    const [pinnedItems, setPinnedItems] = useState(() => {
        // Start with localStorage as cache
        const stored = localStorage.getItem('cabazaPinnedItems');
        if (stored) {
            return JSON.parse(stored);
        }
        // Migrate from old format
        const oldModules = localStorage.getItem('cabazaPinnedModules');
        const oldGroups = localStorage.getItem('cabazaPinnedGroups');
        const items = [];
        if (oldModules) {
            JSON.parse(oldModules).forEach(id => items.push({ type: 'module', id }));
        }
        if (oldGroups) {
            JSON.parse(oldGroups).forEach(id => items.push({ type: 'group', id }));
        }
        return items;
    });

    // Track if we've loaded from API
    const [preferencesLoaded, setPreferencesLoaded] = useState(false);
    // Ref to debounce API saves
    const saveTimeoutRef = useRef(null);

    // Module visibility from localStorage
    const defaultModules = { contacts: true, map: true, agenda: true, messages: true, events: true, places: true };
    const [enabledModules, setEnabledModules] = useState(() => {
        const stored = localStorage.getItem('cabazaModules');
        return stored ? { ...defaultModules, ...JSON.parse(stored) } : defaultModules;
    });

    const userId = parseInt(localStorage.getItem('userId'), 10);

    // Fetch data on mount
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const [modulesData, groupsData, count] = await Promise.all([
                    getModules(language),
                    getPeopleModules(userId, language),
                    getUnreadCount()
                ]);
                // Normalize keys to lowercase (API returns uppercase)
                const normalizedModules = normalizeKeys(modulesData);
                const normalizedGroups = normalizeKeys(groupsData);

                setAllModules(normalizedModules);
                setModuleGroups(normalizedGroups);
                setUnreadMessages(count);

                // Initialize collapsed state from group data
                const collapsed = {};
                normalizedGroups.forEach(g => {
                    collapsed[g.id] = g.iscollapsed || false;
                });
                setCollapsedGroups(collapsed);
            } catch (error) {
                console.error('Error loading modules:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [userId, language]);

    // Listen for modules visibility changes
    useEffect(() => {
        const handleModulesChange = () => {
            const stored = localStorage.getItem('cabazaModules');
            setEnabledModules(stored ? { ...defaultModules, ...JSON.parse(stored) } : defaultModules);
        };
        window.addEventListener('modulesChanged', handleModulesChange);
        return () => window.removeEventListener('modulesChanged', handleModulesChange);
    }, []);

    // Listen for modules/groups changes from Preferences or Sidebar
    useEffect(() => {
        const handleModulesGroupsChange = async () => {
            try {
                const [modulesData, groupsData] = await Promise.all([
                    getModules(language),
                    getPeopleModules(userId, language)
                ]);
                const normalizedGroups = normalizeKeys(groupsData);
                setAllModules(normalizeKeys(modulesData));
                setModuleGroups(normalizedGroups);
                // Update localStorage for sidebar
                localStorage.setItem('cabazaGroupsData', JSON.stringify(normalizedGroups));
            } catch (error) {
                console.error('Error reloading modules:', error);
            }
        };
        window.addEventListener('modulesGroupsChanged', handleModulesGroupsChange);
        return () => window.removeEventListener('modulesGroupsChanged', handleModulesGroupsChange);
    }, [userId, language]);

    // Load preferences from API on mount
    useEffect(() => {
        const loadPreferences = async () => {
            if (userId) {
                try {
                    const prefs = await getPeoplePreferences(userId);
                    const pinnedItemsFromApi = prefs.pinneditems || prefs.pinnedItems || [];
                    if (pinnedItemsFromApi.length > 0) {
                        setPinnedItems(pinnedItemsFromApi);
                        localStorage.setItem('cabazaPinnedItems', JSON.stringify(pinnedItemsFromApi));
                    }
                    setPreferencesLoaded(true);
                } catch (error) {
                    console.error('Error loading preferences:', error);
                    setPreferencesLoaded(true);
                }
            }
        };
        loadPreferences();
    }, [userId]);

    // Save preferences to API when pinned items change (debounced)
    useEffect(() => {
        if (!preferencesLoaded || !userId) return;

        // Clear any pending save
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }

        // Debounce API save (500ms)
        saveTimeoutRef.current = setTimeout(async () => {
            try {
                await savePeoplePreferences(userId, { pinnedItems });
            } catch (error) {
                console.error('Error saving preferences:', error);
            }
        }, 500);

        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
        };
    }, [pinnedItems, preferencesLoaded, userId]);

    // Get icon for module code
    const getModuleIcon = (code) => {
        const icons = {
            'CONTACTS': Users,
            'CONVERSATIONS': MessageCircle,
            'MAP': MapPin,
            'AGENDA': Calendar,
            'EVENTS': PartyPopper,
            'PLACES': Building2
        };
        return icons[code] || LayoutGrid;
    };

    // Get gradient for module code
    const getModuleGradient = (code) => {
        const gradients = {
            'CONTACTS': 'from-blue-500 to-blue-600',
            'CONVERSATIONS': 'from-cabaza-purple to-cabaza-pink',
            'MAP': 'from-green-500 to-green-600',
            'AGENDA': 'from-orange-500 to-orange-600',
            'EVENTS': 'from-pink-500 to-rose-600',
            'PLACES': 'from-teal-500 to-teal-600'
        };
        return gradients[code] || 'from-gray-500 to-gray-600';
    };

    // Handle drag start for module
    const handleDragStart = (e, moduleCode, groupId = null) => {
        e.dataTransfer.setData('moduleCode', moduleCode);
        e.dataTransfer.setData('groupId', groupId || '');
        e.dataTransfer.effectAllowed = 'copy';
    };

    // Handle drag start for group
    const handleGroupDragStart = (e, groupId) => {
        e.dataTransfer.setData('groupId', groupId);
        e.dataTransfer.setData('isGroup', 'true');
        e.dataTransfer.effectAllowed = 'copy';
    };

    // Toggle pin module
    const togglePinModule = (moduleCode) => {
        setPinnedItems(prev => {
            const navId = MODULE_NAV_MAP[moduleCode] || moduleCode.toLowerCase();
            const isPinned = prev.some(i => i.type === 'module' && i.id === navId);
            const newPinned = isPinned
                ? prev.filter(i => !(i.type === 'module' && i.id === navId))
                : [...prev, { type: 'module', id: navId }];
            localStorage.setItem('cabazaPinnedItems', JSON.stringify(newPinned));
            window.dispatchEvent(new CustomEvent('pinnedItemsChanged'));
            return newPinned;
        });
    };

    // Toggle pin group
    const togglePinGroup = (groupId) => {
        setPinnedItems(prev => {
            const isPinned = prev.some(i => i.type === 'group' && i.id === groupId);
            const newPinned = isPinned
                ? prev.filter(i => !(i.type === 'group' && i.id === groupId))
                : [...prev, { type: 'group', id: groupId }];
            localStorage.setItem('cabazaPinnedItems', JSON.stringify(newPinned));
            window.dispatchEvent(new CustomEvent('pinnedItemsChanged'));
            return newPinned;
        });
    };

    // Toggle collapse group
    const toggleCollapseGroup = (groupId) => {
        setCollapsedGroups(prev => ({
            ...prev,
            [groupId]: !prev[groupId]
        }));
    };

    // Navigate to module
    const navigateToModule = (moduleCode) => {
        const navId = MODULE_NAV_MAP[moduleCode] || moduleCode.toLowerCase();
        onNavigate(navId);
    };

    // Get ungrouped modules (not in any group)
    const getUngroupedModules = () => {
        const groupedModuleIds = new Set();
        moduleGroups.forEach(group => {
            group.modules?.forEach(m => groupedModuleIds.add(m.moduleid));
        });
        return allModules.filter(m => !groupedModuleIds.has(m.id));
    };

    // Check if module is enabled
    const isModuleEnabled = (moduleCode) => {
        const key = MODULE_NAV_MAP[moduleCode] || moduleCode.toLowerCase();
        return enabledModules[key] !== false;
    };

    // Check if module is pinned
    const isModulePinned = (moduleCode) => {
        const navId = MODULE_NAV_MAP[moduleCode] || moduleCode.toLowerCase();
        return pinnedItems.some(i => i.type === 'module' && i.id === navId);
    };

    // Check if group is pinned
    const isGroupPinned = (groupId) => {
        return pinnedItems.some(i => i.type === 'group' && i.id === groupId);
    };

    // Toggle module isPinned within a group (visibility in sidebar)
    const handleToggleModuleInGroup = async (groupId, moduleId, currentIsPinned) => {
        const newIsPinned = !currentIsPinned;

        // Optimistic update - update local state immediately
        setModuleGroups(prev => prev.map(group => {
            if (group.id === groupId) {
                return {
                    ...group,
                    modules: group.modules.map(m => {
                        if (m.moduleid === moduleId) {
                            return { ...m, ispinned: newIsPinned ? 1 : 0 };
                        }
                        return m;
                    })
                };
            }
            return group;
        }));

        // Update localStorage
        const stored = localStorage.getItem('cabazaGroupsData');
        if (stored) {
            const groupsData = JSON.parse(stored);
            const updatedGroupsData = groupsData.map(group => {
                if (group.id === groupId) {
                    return {
                        ...group,
                        modules: group.modules.map(m => {
                            if (m.moduleid === moduleId) {
                                return { ...m, ispinned: newIsPinned ? 1 : 0 };
                            }
                            return m;
                        })
                    };
                }
                return group;
            });
            localStorage.setItem('cabazaGroupsData', JSON.stringify(updatedGroupsData));

            // Dispatch specific event for pin toggle (only sidebar listens, not Modules page)
            window.dispatchEvent(new CustomEvent('modulePinnedToggled'));
        }

        // Save to API (don't await to keep UI responsive)
        toggleModulePinned(groupId, moduleId, newIsPinned).catch(error => {
            console.error('Error toggling module pinned status:', error);
        });
    };

    // Handle drop on a group (move module from one group to another)
    const handleDropOnGroup = async (e, targetGroupId) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOverGroupId(null);

        // Get data from dataTransfer (works for both internal drag and from sidebar)
        const moduleCode = e.dataTransfer.getData('moduleCode');
        const sourceGroupId = e.dataTransfer.getData('sourceGroupId') || e.dataTransfer.getData('groupId');
        const moduleIdFromTransfer = e.dataTransfer.getData('moduleId');

        if (!moduleCode) return;

        // Find the module ID from moduleCode or use the one from dataTransfer
        const moduleIdMap = {
            'CONTACTS': 1,
            'CONVERSATIONS': 2,
            'MAP': 3,
            'AGENDA': 4,
            'EVENTS': 5,
            'PLACES': 6
        };
        const numericModuleId = moduleIdFromTransfer ? parseInt(moduleIdFromTransfer) : moduleIdMap[moduleCode];
        if (!numericModuleId) return;

        // Don't do anything if dropping on same group
        if (sourceGroupId && parseInt(sourceGroupId) === targetGroupId) return;

        // Check if module already exists in target group
        const targetGroup = moduleGroups.find(g => g.id === targetGroupId);
        if (targetGroup?.modules?.some(m => m.moduleid === numericModuleId)) {
            return; // Module already in target group
        }

        try {
            // If coming from another group, remove from source first
            if (sourceGroupId) {
                await removeModuleFromGroup(parseInt(sourceGroupId), numericModuleId);
            }

            // Add to target group
            await addModuleToGroup(targetGroupId, numericModuleId);

            // Reload data
            const groupsData = await getPeopleModules(userId, language);
            const normalizedGroups = normalizeKeys(groupsData);
            setModuleGroups(normalizedGroups);

            // Update localStorage and dispatch event
            localStorage.setItem('cabazaGroupsData', JSON.stringify(normalizedGroups));
            window.dispatchEvent(new CustomEvent('modulesGroupsChanged'));
        } catch (error) {
            console.error('Error moving module between groups:', error);
        }
    };

    // Handle drag over group
    const handleGroupDragOver = (e, groupId) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOverGroupId(groupId);
    };

    // Handle drag leave group
    const handleGroupDragLeave = () => {
        setDragOverGroupId(null);
    };

    // Module card component
    const ModuleCard = ({ module, groupId = null, showGrip = true }) => {
        const Icon = getModuleIcon(module.code);
        const navId = MODULE_NAV_MAP[module.code] || module.code.toLowerCase();
        // For modules in groups, use the module's isPinned property
        // For ungrouped modules, use the pinnedItems array
        const isInGroup = groupId !== null;
        const moduleIsPinnedInGroup = module.ispinned === 1 || module.ispinned === true || module.ispinned === undefined || module.ispinned === null;
        const isPinned = isInGroup ? moduleIsPinnedInGroup : isModulePinned(module.code);
        const hasBadge = module.code === 'CONVERSATIONS' && unreadMessages > 0;

        if (!isModuleEnabled(module.code)) return null;

        const handlePinClick = (e) => {
            e.stopPropagation();
            if (isInGroup) {
                // Toggle isPinned for module in group
                handleToggleModuleInGroup(groupId, module.moduleid, moduleIsPinnedInGroup);
            } else {
                // Toggle pin for ungrouped module
                togglePinModule(module.code);
            }
        };

        return (
            <div
                draggable
                onDragStart={(e) => handleDragStart(e, module.code, groupId)}
                className="group relative flex flex-col items-center gap-3 p-6 bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-lg hover:border-transparent hover:scale-105 transition-all duration-300 cursor-grab active:cursor-grabbing"
            >
                {/* Pin button */}
                <button
                    onClick={handlePinClick}
                    className={`absolute top-2 right-2 p-1.5 rounded-full transition-all ${
                        isPinned
                            ? 'bg-cabaza-purple text-white'
                            : 'bg-gray-100 text-gray-400 hover:bg-gray-200 hover:text-gray-600'
                    }`}
                    title={isPinned
                        ? (isInGroup ? (t.modules?.hideFromSidebar || 'Hide from sidebar') : (t.modules?.unpinFromSidebar || 'Unpin from sidebar'))
                        : (isInGroup ? (t.modules?.showInSidebar || 'Show in sidebar') : (t.modules?.pinToSidebar || 'Pin to sidebar'))}
                >
                    <Pin className="w-4 h-4" />
                </button>

                {/* Drag handle */}
                {showGrip && (
                    <div className="absolute top-2 left-2 p-1.5 text-gray-300 group-hover:text-gray-400">
                        <GripVertical className="w-4 h-4" />
                    </div>
                )}

                {/* Clickable area for navigation */}
                <button
                    onClick={() => navigateToModule(module.code)}
                    className="flex flex-col items-center gap-3 w-full"
                >
                    {/* Icon with color background */}
                    <div
                        className="relative w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow"
                        style={{ backgroundColor: module.color || '#8b5cf6' }}
                    >
                        <Icon className="w-8 h-8 text-white" />
                        {hasBadge && (
                            <span className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold shadow-md">
                                {unreadMessages > 9 ? '9+' : unreadMessages}
                            </span>
                        )}
                    </div>

                    {/* Label */}
                    <div className="text-center">
                        <h3 className="font-semibold text-gray-800 group-hover:text-cabaza-purple transition-colors">
                            {module.name || module[`name_${language}`] || module.name_fr}
                        </h3>
                    </div>
                </button>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin w-8 h-8 border-4 border-cabaza-purple border-t-transparent rounded-full"></div>
            </div>
        );
    }

    const ungroupedModules = getUngroupedModules().filter(m => isModuleEnabled(m.code));

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-gray-800">
                    {t.modules?.title || 'Modules'}
                </h1>
                <p className="text-gray-500 mt-1">
                    {t.modules?.subtitle || 'Access your features'}
                </p>
            </div>

            {/* Ungrouped modules */}
            {ungroupedModules.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {ungroupedModules.map((module) => (
                        <ModuleCard key={module.id} module={module} />
                    ))}
                </div>
            )}

            {/* Grouped modules */}
            {moduleGroups.map((group) => {
                const isCollapsed = collapsedGroups[group.id] ?? false;
                const groupPinned = isGroupPinned(group.id);
                const enabledGroupModules = group.modules?.filter(m => isModuleEnabled(m.code)) || [];

                if (enabledGroupModules.length === 0) return null;

                const isDragOver = dragOverGroupId === group.id;

                return (
                    <div
                        key={group.id}
                        className={`bg-white rounded-2xl shadow-sm border overflow-hidden transition-all ${
                            isDragOver ? 'border-cabaza-purple border-2 bg-purple-50' : 'border-gray-100'
                        }`}
                        onDragOver={(e) => handleGroupDragOver(e, group.id)}
                        onDragLeave={handleGroupDragLeave}
                        onDrop={(e) => handleDropOnGroup(e, group.id)}
                    >
                        {/* Group header */}
                        <div
                            draggable
                            onDragStart={(e) => handleGroupDragStart(e, group.id)}
                            className="flex items-center justify-between p-4 border-b border-gray-100 cursor-grab active:cursor-grabbing hover:bg-gray-50"
                        >
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={() => toggleCollapseGroup(group.id)}
                                    className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    {isCollapsed ? (
                                        <ChevronRight className="w-5 h-5 text-gray-500" />
                                    ) : (
                                        <ChevronDown className="w-5 h-5 text-gray-500" />
                                    )}
                                </button>
                                <div
                                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                                    style={{ backgroundColor: group.color || '#8b5cf6' }}
                                >
                                    <Folder className="w-4 h-4 text-white" />
                                </div>
                                <span className="font-semibold text-gray-800">{group.name}</span>
                                <span className="text-sm text-gray-400">({enabledGroupModules.length})</span>
                            </div>

                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    togglePinGroup(group.id);
                                }}
                                className={`p-1.5 rounded-full transition-all ${
                                    groupPinned
                                        ? 'bg-cabaza-purple text-white'
                                        : 'bg-gray-100 text-gray-400 hover:bg-gray-200 hover:text-gray-600'
                                }`}
                                title={groupPinned ? (t.modules?.unpinFromSidebar || 'Unpin from sidebar') : (t.modules?.pinToSidebar || 'Pin to sidebar')}
                            >
                                <Pin className="w-4 h-4" />
                            </button>
                        </div>

                        {/* Group modules */}
                        {!isCollapsed && (
                            <div className="p-4">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    {enabledGroupModules.map((module) => (
                                        <ModuleCard
                                            key={module.itemid}
                                            module={module}
                                            groupId={group.id}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}

            {/* Empty state */}
            {ungroupedModules.length === 0 && moduleGroups.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                    <LayoutGrid className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg font-medium">{t.modules?.noModules || 'No modules available'}</p>
                    <p className="text-sm mt-1">{t.modules?.enableModules || 'Enable modules in Preferences'}</p>
                </div>
            )}
        </div>
    );
};

export default Modules;
